"use client";

import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useSyncExternalStore,
} from "react";
import { createPortal } from "react-dom";
import landUseCodes from "@/data/council/land-use-codes.json";

type Entry = { code: string; name: string; description: string; lubUrl: string | null };

// Build a lookup map from the artefact's data.codes array.
const DISTRICTS = new Map<string, Entry>(
  (landUseCodes.data.codes as Entry[]).map((e) => [e.code.toUpperCase(), e]),
);

export function DistrictCode({ code }: { code: string }) {
  // Parse density-overlay suffix: "F3.5H24" → { far: 3.5, height: 24 }
  const overlayMatch = code.match(/[Ff]([\d.]+)(?:[Hh]([\d.]+))?/);
  const overlay = overlayMatch
    ? {
        far: overlayMatch[1] ? parseFloat(overlayMatch[1]) : null,
        height: overlayMatch[2] ? parseFloat(overlayMatch[2]) : null,
      }
    : null;

  const baseCode = code.replace(/[Ff](?=\d).*$/, "").toUpperCase();
  const def = DISTRICTS.get(baseCode);
  const [open, setOpen] = useState(false);
  const [tipPos, setTipPos] = useState<{ left: number; top: number } | null>(
    null,
  );
  const ref = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLSpanElement>(null);

  // Portals can't render during SSR. `useSyncExternalStore` returns the
  // server snapshot (false) on the server and first hydration pass, then
  // the client snapshot (true) — a hydration-safe "is mounted" flag with
  // no setState-in-effect.
  const mounted = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false,
  );

  // Position the portalled tooltip relative to the trigger's bounding
  // rect. The tooltip is rendered into document.body (escaping any
  // transformed/clipping ancestor like the public-hearings drawer),
  // so the coordinates are viewport-relative `position: fixed`.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const trigger = ref.current;
      const tip = tipRef.current;
      if (!trigger || !tip) return;
      const tRect = trigger.getBoundingClientRect();
      const tipRect = tip.getBoundingClientRect();
      const margin = 8;
      const gap = 7;
      const triggerCx = tRect.left + tRect.width / 2;
      let left = triggerCx - tipRect.width / 2;
      left = Math.max(
        margin,
        Math.min(left, window.innerWidth - tipRect.width - margin),
      );
      let top = tRect.top - tipRect.height - gap;
      if (top < margin) top = tRect.bottom + gap;
      top = Math.max(
        margin,
        Math.min(top, window.innerHeight - tipRect.height - margin),
      );
      setTipPos({ left, top });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target)) return;
      if (tipRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Tooltip is portalled outside the trigger, so leaving the trigger
  // would normally close it immediately — making the Land Use Bylaw
  // link unreachable. We keep open while the pointer is over either
  // the trigger or the tooltip; the closeTimer absorbs the brief gap
  // when crossing between them.
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 80);
  };
  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  useEffect(
    () => () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    },
    [],
  );

  if (!def) return <code>{code}</code>;

  const tooltip = (
    <span
      ref={tipRef}
      className={`ph-district-tip${open ? " ph-district-tip-open" : ""}`}
      role="tooltip"
      aria-hidden={!open}
      onMouseEnter={cancelClose}
      onMouseLeave={scheduleClose}
      style={
        tipPos
          ? { left: `${tipPos.left}px`, top: `${tipPos.top}px` }
          : undefined
      }
    >
      <span className="ph-district-tip-name">{def.name}</span>
      <span className="ph-district-tip-desc">{def.description}</span>
      {overlay && (overlay.far !== null || overlay.height !== null) && (
        <span className="ph-district-tip-overlay">
          {overlay.far !== null && (
            <span>Floor area up to {overlay.far}× the lot size</span>
          )}
          {overlay.height !== null && (
            <span>Up to {overlay.height} m tall</span>
          )}
        </span>
      )}
      {def.lubUrl && (
        <a
          href={def.lubUrl}
          target="_blank"
          rel="noreferrer"
          className="ph-district-tip-link"
          tabIndex={open ? 0 : -1}
        >
          Land Use Bylaw ↗
        </a>
      )}
    </span>
  );

  return (
    <span
      ref={ref}
      className="ph-district"
      onMouseEnter={() => {
        cancelClose();
        setOpen(true);
      }}
      onMouseLeave={scheduleClose}
      onFocus={() => setOpen(true)}
      onBlur={(e) => {
        if (tipRef.current?.contains(e.relatedTarget as Node)) return;
        setOpen(false);
      }}
      tabIndex={0}
      role="button"
      aria-label={`${code} — ${def.name}`}
    >
      <code>{code}</code>
      {mounted && open && createPortal(tooltip, document.body)}
    </span>
  );
}
